import { Button, Group, Modal, Select, Stack, Text, TextInput } from "@mantine/core"
import React from "react"
import { useTranslation } from "react-i18next"
import { SavedListApi } from "../../../apis"
import type { ISavedListLine } from "../../../apis/saved-list"
import { message } from "../../../utils/message"
import { translateBackendError } from "../../delivery/translate-error"

export type SaveListGroup = {
  supplierBusinessId: string
  supplierName: string
  lines: ISavedListLine[]
}

type Props = {
  opened: boolean
  onClose: () => void
  /** One group per supplier; a list belongs to one supplier, so several groups add a picker. */
  groups: SaveListGroup[]
  onSaved?: (id: string) => void
}

const MAX_NAME = 120

/** «Жагсаалт болгон хадгалах» (B25): names the lines and saves them as one supplier's list. */
export function SaveListModal({ opened, onClose, groups, onSaved }: Readonly<Props>) {
  const { t } = useTranslation("bulkOrder")
  const [name, setName] = React.useState("")
  const [picked, setPicked] = React.useState<string | null>(null)
  const [saving, setSaving] = React.useState(false)

  const group = groups.find((g) => g.supplierBusinessId === picked) || groups[0]
  const trimmed = name.trim()

  const handleClose = () => {
    setName("")
    setPicked(null)
    onClose()
  }

  const handleSave = async () => {
    if (!group || !trimmed || saving) return
    setSaving(true)
    try {
      const res = await SavedListApi.create({
        name: trimmed.slice(0, MAX_NAME),
        supplierBusinessId: group.supplierBusinessId,
        lines: group.lines.slice(0, 500),
      })
      message.success(t("saveModal.saved"))
      onSaved?.(res?.id)
      handleClose()
    } catch (err) {
      message.error(translateBackendError(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal opened={opened} onClose={handleClose} title={t("saveModal.title")} centered size="md">
      <Stack gap="md">
        <TextInput
          label={t("saveModal.name")}
          placeholder={t("saveModal.namePlaceholder")}
          value={name}
          maxLength={MAX_NAME}
          required
          data-autofocus
          onChange={(e) => setName(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave()
          }}
        />
        {groups.length > 1 && (
          <Select
            label={t("saveModal.supplier")}
            data={groups.map((g) => ({ value: g.supplierBusinessId, label: g.supplierName }))}
            value={group?.supplierBusinessId || null}
            onChange={setPicked}
            allowDeselect={false}
          />
        )}
        <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
          {t("saveModal.count", { count: group?.lines.length || 0 })}
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={handleClose}>
            {t("saveModal.cancel")}
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={!trimmed || !group || group.lines.length === 0}>
            {t("saveModal.save")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
